import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Building2, Globe, LandPlot, Mountain, Zap, Landmark, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { useTranslation } from "@/hooks/useTranslation";

const OzlemProfile = () => {
  const { t } = useTranslation();
  
  const user = {
    name: "Ozlem Tuncel Toplu",
    role: "Director of Economic Affairs",
    email: "ozlem.tuncel-toplu@mfa.gov.tr",
    solutionBounties: 7,
    domains: [
      "Economic Diplomacy",
      "Trade Relations",
      "Investment Attraction",
      "Energy Policy",
      "International Commerce",
      "Sustainable Development",
      "Public-Private Partnerships",
      "Cross-Border Innovation",
      "Technology Transfer"
    ],
    industries: [
      { name: "Government", icon: Landmark },
      { name: "International Trade", icon: Globe },
      { name: "Energy", icon: Zap },
      { name: "Natural Resources", icon: Mountain },
      { name: "Urban Planning", icon: LandPlot },
      { name: "Economic Development", icon: Building2 }
    ],
    confirmedSolutions: 15,
    confirmedKnowledgeBases: 23,
    tokenBalance: 82,
    bio: "Ozlem Tuncel Toplu serves as the Director of Economic Affairs at the Ministry of Foreign Affairs, where she leads strategic initiatives to strengthen international economic partnerships and investment opportunities. With over 15 years of experience in economic diplomacy, she has spearheaded numerous trade missions and negotiated key economic cooperation agreements. Ozlem specializes in facilitating cross-border innovation ecosystems and sustainable development projects, with particular expertise in energy, technology, and urban development sectors. She works closely with both public and private stakeholders to identify synergies and create mutual value through international collaboration."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png"
                    imageAlt="Ozlem Tuncel Toplu"
                    className="h-36 w-36"
                  >
                    <Avatar className="h-36 w-36 transition-all duration-300 hover:scale-110 cursor-pointer">
                      <AvatarImage src="/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png" alt="Ozlem Tuncel Toplu" className="object-cover" />
                      <AvatarFallback className="bg-blue-100 text-blue-800 text-3xl">OT</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          {t('profile.bio')}
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[500px] p-4 animate-fade-in">
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold">{user.name}</h4>
                          <div className="text-sm text-gray-700 space-y-2">
                            <p>{t('ozlem.bio')}</p>
                            <p>{t('ozlem.bio2')}</p>
                            <p>{t('ozlem.bio3')}</p>
                            <p>{t('ozlem.bio4')}</p>
                            <p>{t('ozlem.bio5')}</p>
                            <p>{t('ozlem.bio6')}</p>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          {t('profile.network')}
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-96 p-4 animate-fade-in">
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold">{t('profile.knowledgeNetwork')}</h4>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/eglantine-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/a095a62e-32dd-481e-94c0-dfa151a021f9.png" alt="Eglantine" className="object-cover" />
                                  <AvatarFallback>EP</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Eglantine</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/karen-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/1747ca6f-b961-4ea7-bae4-a5d133e2a21b.png" alt="Karen" />
                                  <AvatarFallback>KA</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Karen</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/pete-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/75937035-16a7-4dc5-88cc-58dd952f6c60.png" alt="Pete" />
                                  <AvatarFallback>PE</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Pete</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/trey-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/34971d27-7908-4c61-ac9e-881493ec551d.png" alt="Trey" />
                                  <AvatarFallback>TR</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Trey</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/steven-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/10314fa3-311f-4fa5-af44-1e79e6e66f43.png" alt="Steven" className="object-cover" />
                                  <AvatarFallback>SJ</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Steven</span>
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">{t('profile.industries')}</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-lg font-medium text-black">{t('profile.directorEconomicAffairs')}</p>
                    <Link to="/quebec-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d41ea10b-9167-451e-944e-ce5900958bf1.png" 
                        alt="Quebec Economic Development" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.solutionBounties')}</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link
                          to="/solution-bounties/ozlem"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {t('profile.viewDetails')}
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={8.3} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.confirmedSolutions')}</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.knowledgeBases')}</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">{t('profile.domains')}</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OzlemProfile;